// CalendarSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';

import 'flatpickr/dist/flatpickr.min.css';

const CalendarSelector = ({
  addTimeSlot,
  selectedTimeslots,
  activeStep,
  formatDateForDisplay,
  goToStep,
  theme = 'pink' // default theme
}) => {
  const [availableDates, setAvailableDates] = useState(new Set());
  const [timeslots, setTimeslots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarError, setCalendarError] = useState('');
  const calendarRef = useRef(null);
  const flatpickrInstance = useRef(null);

  let styleSel = 'bg-blue-100 text-blue-800 rounded-full';
    let availableTextStyle = "text-gray-800"
  if (theme === 'pink'){
    styleSel = 'bg-pink-primary-600 text-white rounded-full'
    availableTextStyle = "text-pink-secondary-600";
  }
  if(theme === 'blue'){
    styleSel = 'bg-blue-primary-500 text-white';
    availableTextStyle = "text-blue-primary-600"
  }

  // Fetch available dates from the API
  const fetchAvailableDates = async () => {
    try {
      const response = await fetch('/api/timeslots/');
      if (!response.ok) throw new Error('Failed to fetch available dates');
      const data = await response.json();

      // Convert all dates to a consistent YYYY-MM-DD format
      const dateSet = new Set();
      data.dates.forEach(dateStr => {
        // Create a date and get local YYYY-MM-DD format
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        dateSet.add(formattedDate);
      });

      setAvailableDates(dateSet);
      return dateSet;
    } catch (error) {
      console.error('Error fetching available dates:', error);
      setCalendarError('Failed to load available dates. Please try again.');
      return new Set();
    }
  };

  // Fetch timeslots for a specific date
  const fetchTimeslotsForDate = async (date) => {
    try {
      const response = await fetch(`/api/timeslots/${date}/`);
      if (!response.ok) throw new Error('Failed to fetch timeslots');
      const data = await response.json();
      return data.timeslots || [];
    } catch (error) {
      console.error('Error fetching timeslots:', error);
      return [];
    }
  };

  // Handle date selection
  const handleDateSelect = async (selectedDates, dateStr) => {
    setSelectedDate(dateStr);
    const fetchedTimeslots = await fetchTimeslotsForDate(dateStr);
    setTimeslots(fetchedTimeslots);
  };

  // Handle adding a timeslot
  const handleAddTimeSlot = (slot) => {
    addTimeSlot(slot, selectedDate);
  };

  // Initialize calendar
  useEffect(() => {
    const initializeCalendar = async () => {
      try {
        const dates = await fetchAvailableDates();

        if (calendarRef.current) {
          // Create the calendar config
          const config = {
            inline: true,
            minDate: "today",
            dateFormat: "Y-m-d",
            static: true,
            disable: [
              (date) => {
                // Use the same consistent formatting for comparison
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;
                return !dates.has(formattedDate);
              }
            ],
            onChange: handleDateSelect
          };

          // Initialize flatpickr
          flatpickrInstance.current = flatpickr(calendarRef.current, config);

          // Select first available date if there are any
          if (dates.size > 0) {
            const firstAvailable = Array.from(dates)[0];
            flatpickrInstance.current.setDate(firstAvailable);

            // Explicitly fetch and show timeslots for the first date
            setSelectedDate(firstAvailable);
            const initialTimeslots = await fetchTimeslotsForDate(firstAvailable);
            setTimeslots(initialTimeslots);
          }
        }
      } catch (error) {
        console.error('Failed to initialize calendar:', error);
        setCalendarError('Failed to load calendar. Please try again.');
      }
    };

    initializeCalendar();

    // Cleanup
    return () => {
      if (flatpickrInstance.current) {
        flatpickrInstance.current.destroy();
      }
    };
  }, []);

  return (
    <>
      {calendarError && (
        <div id="calendar-error" className="text-red-500 mb-4">{calendarError}</div>
      )}

      {/* Section 1: Date and Time Selection Combined */}
      <section id="date-time-selection" className={`mb-8 pb-8 border-b border-gray-200 ${activeStep === 2 ? 'hidden' : ''}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-medium text-gray-900 ${theme === 'pink' ? 'text-pink-primary-600' : ''}`}>1. Select Date & Time</h2>
          {selectedDate && (
            <div className={`${styleSel} text-sm font-medium px-3 py-1 `}>
              {formatDateForDisplay(selectedDate)}
            </div>
          )}
        </div>

        <div className={`calendar-wrapper mb-8 theme-${theme}`}>
          <div id="calendar" ref={calendarRef} className="w-full"></div>
        </div>

        <div className={`mt-8 ${!selectedDate ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-medium ${availableTextStyle}`}>Available Time Slots</h3>
            {selectedTimeslots.length > 0 && (
              <div className="text-sm text-gray-600">
                {selectedTimeslots.length} time slot{selectedTimeslots.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {timeslots.length > 0 ? (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {timeslots.map(slot => {
                  const isSelected = selectedTimeslots.find(el => el.id === slot.id);
                  let btnStyle = 'bg-gray-100 text-gray-800';
                  let hoverStyle = 'hover:bg-gray-200';

                  if (theme === 'pink') {
                    hoverStyle = ' text-primary-500 hover:bg-pink-primary-500 hover:text-white';
                  } else if (theme === 'blue') {
                    hoverStyle = 'hover:bg-blue-primary-600 hover:text-white';
                  }

                  if (isSelected) {
                    if (theme === 'pink') {
                      btnStyle = 'bg-pink-primary-500 text-white shadow-md';
                      hoverStyle = '';
                    } else if (theme === 'blue') {
                      btnStyle = 'bg-blue-primary-600 text-white shadow-md';
                      hoverStyle = '';
                    } else {
                      btnStyle = 'bg-blue-600 text-white shadow-md';
                      hoverStyle = '';
                    }
                  }

                  return (
                    <button
                      key={slot.id}
                      className={`time-slot-btn px-4 py-3 text-sm font-medium rounded-full transition-all ${btnStyle} ${hoverStyle}`}
                      data-slot-id={slot.id}
                      data-time={slot.start_time}
                      onClick={() => handleAddTimeSlot(slot)}
                    >
                      {slot.formatted_time}
                    </button>
                  );
                })}
              </div>

              <div className={`mt-4 text-sm ${theme === 'blue' ? 'text-blue-primary-600' : 'text-gray-500'}  `}>
                <p>Click multiple time slots to book tickets for different times.</p>
              </div>
            </div>
          ) : selectedDate ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className={`${theme === 'blue' ? 'text-blue-primary-600' : 'text-gray-500'}`}>No timeslots available for this date</p>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className={`${theme === 'blue' ? 'text-blue-primary-600' : 'text-gray-500'}`}>Please select a date first</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CalendarSelector;